import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, of, switchMap } from 'rxjs';
import { SafeSend, SafeSendType } from '@ucx-js/chat-ui';
import { TranslateService } from '@ngx-translate/core';
import { SafeSendActions } from './safe-send.actions';
import { LiveChatActions } from '../live-chat/live-chat.actions';
import { selectSafeSendState } from './safe-send.reducer';
import { Store } from '@ngrx/store';
import { SafesendService } from '../../services/safesend.service';
import { selectSessionIdentifier } from '../va-session/va-session.reducer';
import { OverlayActions } from '../overlay-state/overlay.actions';
import { appConfig } from '../../../init/getAppConfig';
import {
  selectChatId,
  selectDepartmentId,
  selectSafeSendUrl,
} from '../live-chat/live-chat.reducer';
import {
  selectAccountNumber,
  selectCustomerName,
  selectEmailAddress,
} from '../user-info/user-info.reducer';
import { getUserProfileId, isUserSignedIn } from '../../../utils/userProfile';
import { MessagesActions } from '../messages/messages.actions';
import { getSemafoneConfig } from '../../../utils/getSemafoneConfig';
import { selectMessagesState } from '../messages/messages.reducer';
import { cloneDeep } from '../../../utils/cloneDeep';
import { loadCreditCardCommonText } from '../../../utils/loadCreditCardCommonText';
import {
  ActionType,
  ComponentType,
  MessageSubType,
  MessageType,
  OverlayType,
  RealmName,
} from '../../types/enum';
import { getBrand } from '../../../utils/getBrand';

@Injectable({
  providedIn: 'root',
})
export class SafeSendEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private translateService: TranslateService,
    private safesendService: SafesendService
  ) {}

  getSafeSendTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafeSendActions.prepareSafeSend),
      concatLatestFrom(() => this.store.select(selectDepartmentId)),
      switchMap(([, departmentId]) => {
        if (!departmentId) {
          return of(LiveChatActions.createLiveChatIframe());
        }
        const language = this.translateService.currentLang;
        return this.safesendService
          .getProactiveSSFormTemplate(departmentId, language)
          .pipe(
            switchMap((result) =>
              of(SafeSendActions.getTemplateSuccess({ result }))
            ),
            catchError(() => of(LiveChatActions.connectToLiveChat()))
          );
      })
    )
  );

  OnSafeSendTemplateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafeSendActions.getTemplateSuccess),
      switchMap((action) => {
        const questionnaire = action.result?.questionnaire;
        if (!questionnaire?.length) {
          return of(LiveChatActions.connectToLiveChat());
        } else {
          const safeSend = this.createSafeSend(
            SafeSendType.PROACTIVE,
            questionnaire
          );
          return of(SafeSendActions.createSafeSend({ value: safeSend }));
        }
      })
    )
  );

  submitSafesendForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LiveChatActions.setLiveChatState,
        SafeSendActions.sendSafeSendData
      ),
      concatLatestFrom(() => [
        this.store.select(selectSafeSendState),
        this.store.select(selectSessionIdentifier),
        this.store.select(selectChatId),
        this.store.select(selectSafeSendUrl),
      ]),
      filter(([, safeSend]) => safeSend.submitRequested),
      switchMap(([, safeSend, sessionId, chatId, safeSendUrl]) =>
        this.safesendService
          .sendSafeSendForm(
            safeSend.safesendInfoArray,
            chatId,
            sessionId,
            safeSendUrl
          )
          .pipe(
            map(() => SafeSendActions.submitSuccessfully({ value: safeSend })),
            catchError(() => of(SafeSendActions.submitFailed()))
          )
      )
    )
  );

  sendLoggedInUserSafeSend$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LiveChatActions.setLiveChatState),
        concatLatestFrom(() => [
          this.store.select(selectSessionIdentifier),
          this.store.select(selectChatId),
          this.store.select(selectAccountNumber),
          this.store.select(selectEmailAddress),
          this.store.select(selectCustomerName),
        ]),
        filter(() => !!isUserSignedIn()),
        switchMap(([, sessionId, chatId, accountNumber, email, customerName]) =>
          this.safesendService.sendLoggedInUserSafeSend(
            sessionId,
            chatId,
            getUserProfileId(),
            accountNumber,
            email,
            customerName
          )
        )
      ),
    { dispatch: false }
  );

  onSetSafeSendUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LiveChatActions.setSafeSendUrl),
      map((action) => {
        if (appConfig.isSafeSendIframe) {
          return OverlayActions.showOverlay({
            value: OverlayType.Iframe,
          });
        }
        return SafeSendActions.prepareAgentSafeSend({
          safeSendUrl: action.safeSendUrl,
        });
      })
    )
  );

  prepareAgentSafeSend$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafeSendActions.prepareAgentSafeSend),
      switchMap((action) => {
        return this.safesendService.getQuestionnaire(action.safeSendUrl).pipe(
          map((result) => {
            const questionnaire = result?.questionnaire;
            if (questionnaire?.length > 0) {
              const safeSend = this.createSafeSend(
                SafeSendType.NORMAL,
                questionnaire
              );
              return SafeSendActions.createSafeSend({ value: safeSend });
            }
            throw new Error('No questionnaire found');
          }),
          catchError(() =>
            of(
              OverlayActions.showOverlay({
                value: OverlayType.Iframe,
              })
            )
          )
        );
      })
    )
  );

  createSafeSendMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafeSendActions.createSafeSend),
      map((action) => {
        const safeSend = action.value;
        const message = <Message>{
          time: new Date(),
          messageSubType: MessageSubType.SAFESEND,
          messageComponent: {
            component: ComponentType.SafeSendBubbleComponent,
            componentData: {
              isCancelBtnHidden: getBrand().name === RealmName.shaw,
              safeSendTitle: this.translateService.instant('SAFESEND.TITLE'),
              safeSendDescription: this.translateService.instant(
                'SAFESEND.DESCRIPTION'
              ),
              submitText: this.translateService.instant('SAFESEND.SUBMIT'),
              skipText:
                safeSend.type == SafeSendType.PROACTIVE
                  ? this.translateService.instant('SAFESEND.SKIP_SUBMIT')
                  : this.translateService.instant('SAFESEND.CANCEL'),
              requiredErrorMessage: this.translateService.instant(
                'SAFESEND.REQUIRED_FIELD'
              ),
              emailErrorMessage: this.translateService.instant(
                'SAFESEND.INVALID_EMAIL'
              ),
              safeSend: safeSend,
              creditCardConfig: {
                creditCardFormData: loadCreditCardCommonText(
                  this.translateService
                ),
                semafoneConfig: getSemafoneConfig(),
              },
            },
            actions: {
              onUpdateSafeSendStatus: ActionType.UpdateSafeSendStatus,
            },
          },
        };
        return MessagesActions.addNewMessages({ messages: [message] });
      })
    )
  );

  updateSafesendSubmittedMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafeSendActions.updateSafeSend),
      concatLatestFrom(() => this.store.select(selectMessagesState)),
      switchMap(([action, messages]) => {
        const isProactive = action.value.type == SafeSendType.PROACTIVE;
        if (action.value.submitRequested) {
          const updatedMessage: Message = cloneDeep(
            messages[messages.length - 1]
          );
          updatedMessage.messageComponent.componentData = {
            ...updatedMessage.messageComponent.componentData,
            isSubmitted: true,
            safeSendDescription: isProactive
              ? this.translateService.instant('SAFESEND.SUBMITTED')
              : this.translateService.instant(
                  'SAFESEND.REQUESTED_CUSTOMER_DETAILS'
                ),
          };
          if (isProactive) {
            return of(
              MessagesActions.updateLastMessage({
                updatedMessage: updatedMessage,
              }),
              LiveChatActions.connectToLiveChat()
            );
          } else {
            return of(
              MessagesActions.updateLastMessage({
                updatedMessage: updatedMessage,
              }),
              SafeSendActions.sendSafeSendData()
            );
          }
        } else {
          if (isProactive) {
            return of(
              MessagesActions.removeLastNMessages({ numberOfItems: 1 }),
              LiveChatActions.connectToLiveChat()
            );
          } else {
            return of(
              MessagesActions.removeLastNMessages({ numberOfItems: 1 })
            );
          }
        }
      })
    )
  );

  createSafeSendConfirmationMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SafeSendActions.createConfirmationMessage),
      map(() => {
        return MessagesActions.addSimpleDialogMessage({
          messageText: this.translateService.instant(
            'SAFESEND.REQUESTED_CUSTOMER_DETAILS'
          ),
          messageType: MessageType.OPERATOR,
        });
      })
    )
  );

  createSafeSend(type: SafeSendType, fields: any): SafeSend {
    const safeSend = new SafeSend();
    safeSend.type = type;
    safeSend.fields = fields;
    return safeSend;
  }
}
